import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'How to Follow Up After a Support-Raising Appointment (Without Being Pushy)';
const DESCRIPTION =
    "A practical guide to following up after a support-raising meeting — what to send, when to send it, and how to ask for a decision without sounding like you're chasing someone down.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover1.jpg',
            datePublished: '2026-07-28',
            dateModified: '2026-07-28',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/follow-up-after-support-raising-appointment',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'How soon should I follow up after a support-raising meeting?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Within 24 to 48 hours, while the conversation is still fresh. A short thank-you note that references something specific from the meeting keeps you top of mind without pressuring for an immediate answer.',
                    },
                },
                {
                    '@type': 'Question',
                    name: "What if someone said they'd think about it and never responds?",
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Give them two to three weeks, then send one direct, low-pressure check-in asking if they have any questions you can answer. Most people who go quiet are avoiding an awkward 'no,' not ignoring you — a gentle, specific nudge makes it easier for them to respond either way.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How many times is it okay to follow up before it feels pushy?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Two follow-ups after the initial thank-you is a reasonable ceiling for most relationships — one two-to-three-week check-in, then one final note a month or so later that explicitly says you won't keep asking. The tone, not the count, is what makes a follow-up feel pushy: a note that offers an easy out reads as considerate even on the third try.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Should I ask for a yes-or-no answer directly?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Yes, eventually — vague follow-ups produce vague non-answers. After one or two soft check-ins, it's fair and clarifying to ask plainly whether they'd like to commit, need more information, or would rather not be asked again. A direct question respects their time as much as yours.",
                    },
                },
            ],
        },
    ],
};

export default function FollowUpAfterSupportRaisingAppointment() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/follow-up-after-support-raising-appointment' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-follow-up-appointment-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover1.jpg" alt="A handwritten thank-you note and a cup of coffee on a table, representing a missionary following up after a support-raising meeting" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>The meeting went well. They asked good questions, nodded at the right moments, and said they'd "think about it and get back to you." Then two weeks pass and you hear nothing. Most missionaries lose more potential support to a weak follow-up than to an actual no — not because people changed their minds, but because life got busy and nobody made it easy to say yes. A good follow-up plan turns a vague, well-intentioned "I'll think about it" into an actual decision, without making the relationship feel transactional.</p>

                    <h2>Send a thank-you within 48 hours, no matter what</h2>

                    <p>Every appointment — whether it ended in a firm commitment or an open "let me pray about it" — deserves a short thank-you within a day or two. Reference something specific from the conversation (a question they asked, a detail about their family, something they shared about their own faith journey) so it doesn't read as a template. This note isn't an ask; it's relationship maintenance, and it's the single easiest follow-up step to skip when you're juggling a dozen appointments in a week.</p>

                    <h2>Give an undecided "yes" room to become a real commitment</h2>

                    <p>When someone says they need to think, pray, or talk with a spouse, resist the urge to close the loop immediately. Instead, tell them plainly when you'll check back in — "I'll follow up in about two weeks, no pressure" — and then actually wait that long. Naming the timeline upfront does two things: it removes the awkwardness of wondering when it's okay to reach out again, and it signals that you respect their process instead of chasing a fast answer.</p>

                    <h2>Make the check-in easy to respond to</h2>

                    <p>When the two-to-three-week mark arrives, send a short, specific message rather than a generic "just checking in!" — offer to answer any remaining questions, share a one-line ministry update, or simply ask if they've had a chance to talk it over. A message that gives them something concrete to respond to gets far more replies than an open-ended nudge, and it reads as helpful rather than as pressure.</p>

                    <h2>Read the difference between "not yet" and "no"</h2>

                    <p>Silence after a second follow-up usually means one of three things: genuine busyness, a soft no they haven't found the words for, or a real objection they're uncomfortable raising (the amount, the cause, a competing commitment). A direct, kind question — "Totally understand if now isn't the right time. Would you rather I follow up in a few months, or would you prefer I not ask again?" — surfaces the real answer and gives them an easy, guilt-free way to decline. See <a href="/resources/handling-no-when-asking-for-support">how to handle a no when asking for support</a> for what to do once you have that answer.</p>

                    <h2>Set a final, respectful close-out point</h2>

                    <p>After one thank-you and two check-ins with no response, it's reasonable to send a final note that explicitly closes the loop: thank them for considering it, let them know the door stays open if anything changes, and move on without a third or fourth follow-up. Continuing to ask past this point tends to damage the relationship far more than a graceful no ever would — and a warm, no-pressure exit often keeps the relationship open for a future ask that a nagging one would have closed permanently.</p>

                    <h2>Keep a simple follow-up tracker</h2>

                    <p>You don't need software for this — a spreadsheet with a name, meeting date, last-contact date, and next-action date is enough to make sure nobody falls through the cracks. Support raising fails as much from disorganization as from actual refusals: a warm "yes, eventually" contact who never gets a second touch is functionally the same as a no. Reviewing this list weekly is one of the highest-leverage habits in a support-raising season.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>How soon should I follow up after a support-raising meeting?</strong><br />
                    Within 24 to 48 hours with a specific, non-templated thank-you note.</p>

                    <p><strong>What if someone said they'd think about it and never responds?</strong><br />
                    Wait two to three weeks, then send one direct, low-pressure check-in that offers something concrete to respond to.</p>

                    <p><strong>How many times is it okay to follow up before it feels pushy?</strong><br />
                    Two follow-ups after the thank-you is a reasonable ceiling — tone matters more than count, and offering an easy out keeps even a third note from feeling pushy.</p>

                    <p><strong>Should I ask for a yes-or-no answer directly?</strong><br />
                    Yes, eventually — a plain, kind question respects their time and clarifies where you actually stand.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/handling-no-when-asking-for-support">How to Handle a 'No' When Asking for Missionary Support</a></li>
                        <li><a href="/resources/raise-support-fast-30-60-90-day-plan">How to Raise Support Fast: A 30/60/90-Day Plan for New Missionaries</a></li>
                        <li><a href="/resources/how-to-write-a-missionary-support-letter">How to Write a Missionary Support Letter That Actually Gets You Funded</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We wrote this after seeing the same pattern over and over: missionaries losing support not to a real no, but to a follow-up that never happened.</p>
                    <p><small><em>Last updated: 2026-07-28.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/follow-up-after-support-raising-appointment" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
